import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { SlideBucketsDTO } from '../../../common/models/slide-buckets.model';
import { SlideBucketStatus } from '../../../common/models/enums/slide-bucket-status.enum';
import { SlideBucketCard } from '../../../common/models/slide-bucket-card.model';
import { PaddingPosition } from '../../../common/directives';
import { CdkDragDrop, transferArrayItem } from '@angular/cdk/drag-drop';

@Component({
    selector: 'lib-buckets-widget',
    templateUrl: './buckets-widget.component.html',
    styleUrls: ['./buckets-widget.component.scss'],
    standalone: false
})
export class BucketsWidgetComponent implements OnChanges {

  @Input() buckets!: SlideBucketsDTO[];
  @Input() shuffle = true;
  @Input() slide: any;
  @Input() isTranslation = false;
  @Input() showEditButton = false;
  @Input() spacing!: PaddingPosition;
  @Input() showErrors = false;
  @Input() animationOptions: { enabled: boolean, animation: any, delay: number } = {
    enabled: true,
    animation: '',
    delay: 0
  };

  @Output() edit: EventEmitter<{bucketIndex: number, cardIndex: number}> = new EventEmitter();
  @Output() remove: EventEmitter<{bucketIndex: number, cardIndex: number}> = new EventEmitter();
  @Output() interactionCompleted: EventEmitter<number> = new EventEmitter();

  public cardsArray: SlideBucketCard[] = [];
  public leftBucketCards = [];
  public rightBucketCards = [];
  public remainingCards = 1;
  public totalCards = 0;
  public leftBucketStatus: SlideBucketStatus = SlideBucketStatus.DEFAULT;
  public rightBucketStatus: SlideBucketStatus = SlideBucketStatus.DEFAULT;
  public readonly LEFT_BUCKET_CODE = 'A';
  public readonly RIGHT_BUCKET_CODE = 'B';
  public readonly statuses: typeof SlideBucketStatus = SlideBucketStatus;

  private clearStatusTimeout: any;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['buckets'] || changes['shuffle']) {
      this.setCardsArray(this.buckets);
    }
  }

  public moveCardToBucket(list = []): void {
    if (this.showEditButton) {
      return;
    }

    const correct = this.checkBucketIsCorrectAndSetStatus(this.cardsArray[this.cardsArray?.length - 1], list);

    if (!correct) {
      return;
    }

    transferArrayItem(this.cardsArray, list, this.cardsArray.length, list.length);
    this.cardMoved();
  }

  public drop(event: CdkDragDrop<any>): void {
    const index = event.previousContainer.data.length;

    const correct = this.checkBucketIsCorrectAndSetStatus(event.previousContainer.data[index - 1], event.container.data);
    if (!correct) {
      return;
    }

    transferArrayItem(event.previousContainer.data, event.container.data, index, event.container.data.length);

    this.cardMoved();
  }

  public trackByFn(_index: number, item: SlideBucketCard): string {
    return item.id;
  }

  public emitEdit(index: number, event?: any): void {
    event?.stopPropagation();
    this.edit.emit(this.resolveCardPosition(index));
  }

  public onBucketEdit(bucketIndex: number): void {
    this.edit.emit({bucketIndex, cardIndex: 0 });
  }

  public emitRemove(index: number, event?: any): void {
    event?.stopPropagation();
    this.remove.emit(this.resolveCardPosition(index));
  }

  private resolveCardPosition(index: number): {bucketIndex: number, cardIndex: number} {
    const bucketIndex = this.cardsArray[index]?.parentIndex || 0;
    const isLeftBucketCard = bucketIndex === 0;
    const leftBucketLength = this.buckets?.[0]?.cards?.length || 1;
    const cardIndex = isLeftBucketCard ? index : index - leftBucketLength;
    return {bucketIndex, cardIndex};
  }

  private checkBucketIsCorrectAndSetStatus(currentCard: SlideBucketCard, list: { id: string }[]): boolean {
    if (!currentCard) {
      return false;
    }

    clearTimeout(this.clearStatusTimeout);
    const isLeftBucketCard = this.isMemberOfBucket(this.buckets?.[0], currentCard);
    const isRightBucketCard = this.isMemberOfBucket(this.buckets?.[1], currentCard);

    this.leftBucketStatus = list === this.leftBucketCards
      ? isLeftBucketCard ? SlideBucketStatus.CORRECT : SlideBucketStatus.INCORRECT
      : SlideBucketStatus.DEFAULT;

    this.rightBucketStatus = list === this.rightBucketCards
      ? isRightBucketCard ? SlideBucketStatus.CORRECT : SlideBucketStatus.INCORRECT
      : SlideBucketStatus.DEFAULT;

    this.clearStatusTimeout = setTimeout(() => {
      this.leftBucketStatus = this.rightBucketStatus = SlideBucketStatus.DEFAULT;
    }, 3500);

    return list === this.leftBucketCards ? isLeftBucketCard : isRightBucketCard;
  }

  private cardMoved(): void {
    this.remainingCards = this.cardsArray.length;
    this.interactionCompleted.emit((this.totalCards - this.remainingCards) / this.totalCards);
  }

  private setCardsArray(buckets: SlideBucketsDTO[]): void {
    this.cardsArray = this.shuffleArray(structuredClone(buckets).reduce<SlideBucketCard[]>((cards, bucket, bucketIndex) => {
      if (bucket.cards?.length) {
        cards.push(...bucket.cards.map(card => { card.parentIndex = bucketIndex; return card; }));
      }

      return cards;
    }, []));

    this.remainingCards = this.cardsArray.length;
    this.totalCards = this.cardsArray.length;
  }

  private shuffleArray(array: any[]) {
    if (!this.shuffle) {
      return array;
    }

    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  private isMemberOfBucket(bucket: SlideBucketsDTO, card: SlideBucketCard): boolean {
    return bucket.cards?.findIndex(bucketCard => bucketCard?.id === card.id) !== -1;
  }

}
